import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, CreditCard, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { AddCard } from "@/components/pages/billing/AddCard";
import {
  useCreateIdentityVerificationSessionMutation,
  useCreateSubscriptionMutation,
  useGetBillingDetailsQuery,
  useGetHomeQuery,
  useGetPaymentMethodsQuery,
  useGetSubscriptionPreviewQuery,
  useUpdateSubscriptionMutation,
} from "@/store/rest_api";
import { BillingInterval, SubscriptionPlan } from "@/model/interface/SubscriptionPlan";
import { PaymentMethodData } from "@/model/interface/PaymentMethodData";
import { cn } from "@/lib/utils";
import { formatCardBrand } from "@/util/payment";
import { formatInvoiceMoney } from "@/util/invoice";
import { apiErrorMessage } from "@/util/apiError";

interface CheckoutDialogProps {
  plan: SubscriptionPlan;
  interval: BillingInterval;
  currentPlan?: SubscriptionPlan;
  currentInterval: BillingInterval;
  onClose: () => void;
}

interface ChecklistRowProps {
  done: boolean;
  title: string;
  detail?: string;
  children?: React.ReactNode;
}

function ChecklistRow({ done, title, detail, children }: ChecklistRowProps) {
  return (
    <div className="flex items-start gap-3 py-3">
      <span
        className={cn(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
          done ? "bg-success-soft text-success-soft-foreground" : "bg-muted text-muted-foreground",
        )}
      >
        {done ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <p className="text-sm font-medium">{title}</p>
          {detail && <p className="text-sm text-muted-foreground">{detail}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

export function CheckoutDialog({ plan, interval, currentPlan, currentInterval, onClose }: CheckoutDialogProps) {
  const navigate = useNavigate();
  const { data: user } = useGetHomeQuery();
  const isNewSubscription = !currentPlan || !user?.activeOrganisation?.stripeSubscriptionId;
  const chargesNow = isNewSubscription || interval !== currentInterval;
  const { data: billingDetails } = useGetBillingDetailsQuery();
  const { data: paymentMethods, isLoading: isLoadingCards } = useGetPaymentMethodsQuery();
  const {
    data: preview,
    isFetching: isFetchingPreview,
    isError: previewFailed,
  } = useGetSubscriptionPreviewQuery({ plan: plan.slug, interval }, { skip: !chargesNow });
  const [createSubscription, { isLoading: isCreating }] = useCreateSubscriptionMutation();
  const [updateSubscription, { isLoading: isUpdating }] = useUpdateSubscriptionMutation();
  const [createVerificationSession, { isLoading: isStartingVerification }] =
    useCreateIdentityVerificationSessionMutation();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [isAddingCard, setIsAddingCard] = useState(false);

  const kycVerified = user?.activeOrganisation?.kycVerificationStatus === "VERIFIED";
  const hasBillingAddress = !!billingDetails?.billingAddressLine1?.trim() && !!billingDetails?.billingPostalCode?.trim();
  const billingAddress = [
    billingDetails?.billingAddressLine1,
    billingDetails?.billingCity,
    [billingDetails?.billingState, billingDetails?.billingPostalCode].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const defaultCard = paymentMethods?.find((method) => method.isDefault);
  const intervalWord = interval === "ANNUAL" ? "year" : "month";
  const price = interval === "ANNUAL" && plan.annualBasePrice ? plan.annualBasePrice : plan.monthlyBasePrice;
  const isSaving = isCreating || isUpdating;
  const isReady = isNewSubscription
    ? kycVerified && hasBillingAddress && !!selectedCardId
    : !chargesNow || hasBillingAddress;
  const title = isNewSubscription
    ? `Subscribe to ${plan.name}`
    : plan.slug === currentPlan?.slug
      ? `Switch to ${interval === "ANNUAL" ? "annual" : "monthly"} billing`
      : `Switch to ${plan.name}`;

  const cardLabel = (method: PaymentMethodData) => `${formatCardBrand(method.card?.brand)} ending ${method.card?.last4}`;

  const handleStartVerification = async () => {
    try {
      const session = await createVerificationSession().unwrap();
      window.location.href = session.url;
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to start verification"));
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    try {
      if (isNewSubscription) {
        if (!selectedCardId) {
          return;
        }
        await createSubscription({ plan: plan.slug, paymentMethodId: selectedCardId, interval }).unwrap();
        toast.success(`Subscribed to the ${plan.name} plan`);
      } else {
        await updateSubscription({ plan: plan.slug, interval }).unwrap();
        toast.success(`Switched to the ${plan.name} plan`);
      }
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, isNewSubscription ? "Could not subscribe" : "Could not switch plans"));
    }
  };

  useEffect(() => {
    if (!selectedCardId && defaultCard) {
      setSelectedCardId(defaultCard.id);
    }
  }, [defaultCard, selectedCardId]);

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto text-foreground">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="tabular-nums">
            {plan.pricingMode === "AT_COST"
              ? `$${price} list price, discounted 100% on every invoice. Usage bills at carrier rates.`
              : `$${price} per ${intervalWord}, billed ${interval === "ANNUAL" ? "annually" : "monthly"}`}
          </DialogDescription>
        </DialogHeader>

        {chargesNow && (
          <div className="divide-y border-y">
            {isNewSubscription && (
              <ChecklistRow done={kycVerified} title={kycVerified ? "Identity verified" : "Verify your identity"}>
                {!kycVerified && (
                  <Button size="sm" variant="outline" onClick={handleStartVerification} disabled={isStartingVerification}>
                    Start verification
                  </Button>
                )}
              </ChecklistRow>
            )}
            <ChecklistRow
              done={hasBillingAddress}
              title="Billing address"
              detail={hasBillingAddress ? billingAddress : "Needed for your invoices and sales tax."}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/manage-organisation/billing/overview")}
              >
                {hasBillingAddress ? "Change" : "Add billing address"}
              </Button>
            </ChecklistRow>
            {isNewSubscription ? (
              <ChecklistRow
                done={!!selectedCardId}
                title="Pay with"
                detail={!isLoadingCards && !paymentMethods?.length ? "Add a card to continue." : undefined}
              >
                {!!paymentMethods?.length && (
                  <RadioGroup value={selectedCardId ?? ""} onValueChange={setSelectedCardId} className="space-y-2">
                    {paymentMethods.map((method) => (
                      <Label
                        key={method.id}
                        htmlFor={`checkout-card-${method.id}`}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-lg border p-3 font-normal",
                          selectedCardId === method.id && "border-primary bg-primary/5",
                        )}
                      >
                        <RadioGroupItem value={method.id} id={`checkout-card-${method.id}`} />
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1">{cardLabel(method)}</span>
                        {method.isDefault && <span className="text-xs text-muted-foreground">Default</span>}
                      </Label>
                    ))}
                  </RadioGroup>
                )}
                <Dialog open={isAddingCard} onOpenChange={setIsAddingCard}>
                  <Button size="sm" variant="outline" onClick={() => setIsAddingCard(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add card
                  </Button>
                  <DialogContent>
                    <AddCard onSuccess={() => setIsAddingCard(false)} />
                  </DialogContent>
                </Dialog>
              </ChecklistRow>
            ) : (
              defaultCard && <ChecklistRow done title="Pay with" detail={cardLabel(defaultCard)} />
            )}
          </div>
        )}

        {chargesNow ? (
          isFetchingPreview ? (
            <Skeleton className="h-20 w-full" />
          ) : previewFailed || !preview ? (
            <p className="text-sm text-muted-foreground">
              We couldn't calculate the total right now. Sales tax is added when you confirm.
            </p>
          ) : (
            <div className="space-y-1.5 text-sm tabular-nums">
              {preview.lines.map((line) => (
                <React.Fragment key={`${line.description}-${line.amount}`}>
                  <div className="flex justify-between gap-4">
                    <span>{line.description}</span>
                    <span>{formatInvoiceMoney(line.amount + line.discount)}</span>
                  </div>
                  {line.discount > 0 && (
                    <div className="flex justify-between gap-4 text-muted-foreground">
                      <span>{line.discountDescription ?? "Discount"}</span>
                      <span>{formatInvoiceMoney(-line.discount)}</span>
                    </div>
                  )}
                </React.Fragment>
              ))}
              <div className="flex justify-between gap-4">
                <span>Sales tax</span>
                <span>{formatInvoiceMoney(preview.tax)}</span>
              </div>
              <div className="flex justify-between gap-4 border-t pt-2 text-base font-semibold">
                <span>Due today</span>
                <span>{formatInvoiceMoney(preview.total)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Renews every {intervalWord} until you cancel.</p>
            </div>
          )
        ) : (
          <p className="text-sm text-muted-foreground">
            Your plan changes right away. The difference for the rest of this billing period is added to your next
            invoice, with its sales tax.
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isReady || isSaving}>
            {isSaving ? "Processing..." : title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
